import React from 'react';
import PropTypes from 'prop-types';
import { FrameCard } from '@weezevent/nacre';
import { Icon, Button } from 'semantic-ui-react';
import { Trans, useTranslation } from 'react-i18next';
import ModeSelector from './modeSelector';
import ResaleException from './resaleException';

/**
 * Return the config block
 * @param eventList : Array -> list of event
 * @param organizationId : int -> orga ID
 * @param mainConfig : Array -> Config on organization ( event , rate and group = null)
 * @param setMainConfig : action to perform on change main config ( on changing resale mode)
 * @param configExceptions : Array -> All configs of organizer ( except the main config)
 * @param setConfigExceptions : action to perform to set all modifications
 * @param setConfigExceptionsToDelete : action to perform to delete some configs ( on click on trash )
 */
export default function ResaleConfigBlock({
    eventList,
    organizationId,
    mainConfig,
    onResaleModeChange,
    configExceptions,
    setConfigExceptions,
    setConfigExceptionsToDelete,
    hasActiveReseller = false
}) {
    const { t } = useTranslation();
    let [configExceptionsByEvent, setConfigExceptionsByEvent] = React.useState([]);

    React.useEffect(() => {
        let exceptionEventTmp = [];
        configExceptions.forEach((config, i) => {
            let event_filtered = eventList.find(event => event.id === config.event);
            if (!event_filtered) {
                return;
            }

            const config_copy = { ...config };
            config_copy.index = i;
            // Get index of configEvent to add him config or create new configEvent
            let index = exceptionEventTmp.findIndex(configEvent => configEvent.length && configEvent[0].event === config.event);
            if (index === -1) {
                exceptionEventTmp.push([config_copy]);
            } else {
                exceptionEventTmp[index].push(config_copy);
            }
        });
        setConfigExceptionsByEvent(exceptionEventTmp);
    }, [mainConfig, configExceptions, eventList]);

    /**
     * On changing mode between enabled on all events by default or disabled on all events.
     */
    const onChangeMode = React.useCallback(
        value => {
            onResaleModeChange({ ...mainConfig, enabled: value });
        },
        [mainConfig, onResaleModeChange]
    );

    const onNewConfigEvent = React.useCallback(() => {
        setConfigExceptionsByEvent([...configExceptionsByEvent, []]);
    }, [configExceptionsByEvent]);

    const onModifyException = React.useCallback(
        (index, configs) => {
            let newConfigs = [...configExceptions];
            configs.forEach(config => {
                let already_exists_index = newConfigs.findIndex(({ event, rate, group }) => {
                    return event === config.event && rate === config.rate && group === config.group;
                });
                let already_exists = newConfigs[already_exists_index];
                if (already_exists) {
                    if (config.deleted) {
                        newConfigs.splice(already_exists_index, 1);
                    } else {
                        already_exists.enabled = config.enabled;
                    }
                } else {
                    newConfigs.push(config);
                }
            });
            setConfigExceptions(newConfigs);
        },
        [configExceptions]
    );

    const onDeleteException = React.useCallback(
        index => {
            // Get all deletable exceptions (having an id)
            let filteredExceptions = configExceptionsByEvent[index].filter(config => config.id);
            setConfigExceptionsToDelete(filteredExceptions);

            // These correspond to config_copies so we can get their original indexes
            let indexes_to_delete = configExceptionsByEvent[index].map(config => config.index);
            let newExceptions = [...configExceptions];
            let deleted = 0;
            for (index of indexes_to_delete) {
                newExceptions.splice(index - deleted, 1);
                deleted++;
            }

            setConfigExceptions(newExceptions);
        },
        [configExceptions, configExceptionsByEvent, setConfigExceptions]
    );

    return (
        <FrameCard title={t('ticket.resales.config.title')} subtitle={t('ticket.resales.config.subtitle')}>
            <ModeSelector resaleMode={mainConfig.enabled} onChangeMode={onChangeMode} />
            <div className={'sub_header_title'}>
                <span className={'sub_header_title_warning'}>
                    {mainConfig.enabled ? (
                        <Trans i18nKey="ticket.resales.config.exception_activated_case">activated mode</Trans>
                    ) : (
                        <Trans i18nKey="ticket.resales.config.exception_disabled_case">disabled mode</Trans>
                    )}
                </span>
            </div>

            {configExceptionsByEvent.map((exception, index) => {
                return (
                    <ResaleException
                        key={index}
                        eventList={eventList}
                        configs={exception}
                        configExceptions={configExceptions}
                        organizationId={organizationId}
                        indiceException={index}
                        setExceptions={onModifyException}
                        deleteException={onDeleteException}
                        resaleMode={mainConfig.enabled}
                    />
                );
            })}
            {hasActiveReseller && (
                <Button name="plus" disabled={mainConfig.enabled === null} className="button_add" primary onClick={onNewConfigEvent}>
                    <Icon name="plus" /> {mainConfig.enabled ? t('ticket.resales.config.add_exception_mode_enabled') : t('ticket.resales.config.add_exception_mode_disabled')}
                </Button>
            )}
        </FrameCard>
    );
}

ResaleConfigBlock.propTypes = {
    organizationId: PropTypes.string.isRequired,
    eventList: PropTypes.array.isRequired,
    mainConfig: PropTypes.object.isRequired,
    hasActiveReseller: PropTypes.bool.isRequired,
    onResaleModeChange: PropTypes.func.isRequired,
    configExceptions: PropTypes.array.isRequired,
    setConfigExceptions: PropTypes.func.isRequired,
    setConfigExceptionsToDelete: PropTypes.func.isRequired
};
